/**
 * PROTECTED FILE — DO NOT MODIFY WITHOUT PERMISSION
 *
 * Last verified working: 2026-01-03
 *
 * This file controls: global game state and context providers
 * Changes here have historically broken core game function.
 *
 * Before modifying:
 * 1. Get explicit approval from Zach
 * 2. Create a checkpoint commit
 * 3. Test full baseline after ANY change
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { debug } from "../../lib/debug.js";
import { createReputationState } from "../../systems/ReputationEngine.js";
import { createEOSState, recordCancellation } from "../../systems/EOSSystem.js";
import { createRelationsState } from "../../systems/RelationsEngine.js";
import { createConsultantState } from "../../systems/ConsultantsSystem.js";
import { createCrewState } from "../../systems/CrewMarketSystem.js";
import { createAdvertisingState } from "../../systems/AdvertisingSystem.js";
import { createRebrandingState } from "../../systems/RebrandingSystem.js";
import { createTechState } from "../../systems/TechSystems.js";
import { createSupportCrewState } from "../../systems/SupportCrewSystem.js";
import { createFuelState, tickFuelPriceDaily, processMonthlyFuel } from "../../systems/FuelSystem.js";
import { depleteRoute, getRouteKey } from "../../systems/RouteDemandSystem.js";
import { applyEarningsBuffs } from "../../systems/EarningsEngine.js";
import { generateSimulationSlots, SLOT_STATUS } from "../../systems/SlotSchedulingSystem.js";
import { checkFleetUnlocks, DEFAULT_FLEET_UNLOCKS } from "../../systems/FleetProgressionSystem.js";
import { AIRPORTS, calculateDistance } from "../../data/airports.js";
import { AIRPORTS as FULL_AIRPORTS } from "../../systems/AirportDatabase.js";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { weatherService } from "../../services/WeatherService.js";
import { assessWeatherImpact } from "../../systems/WeatherImpact.js";
import { generateCrewPool } from "../../data/crew.js";
import { saveGame, loadGame, deleteSave, getSteamUser } from "../../lib/saveManager.js";
// import { syncSteamLeaderboards } from "../../lib/steam/leaderboards.js"; // Using direct approach instead

// ============================================================================
// WORLD STATE - Single source of truth for the entire game
// ============================================================================

// Default hub - Portland
const DEFAULT_HUB = "KPDX";

// NO STARTER FLEET - Player must lease/buy from market (per GDD)
const STARTER_FLEET = [];

// STARTER CREW - Small initial pool to get started
// Generate crew with type ratings for regional aircraft (E170, E175, CRJ700, CRJ900)
const STARTER_CREW = generateCrewPool({
  captains: 8,
  firstOfficers: 10,
  flightAttendants: 20,
  homeBase: DEFAULT_HUB
});

// Starter routes - Tier 1 regional triangle from Portland
// Keep it TIGHT - just the local PNW network to start
// Enhanced for Simple mode: aircraftId, autoDispatch, status
const STARTER_ROUTES = [
  {
    id: "KPDX-KSEA",
    origin: "KPDX",
    destination: "KSEA",
    name: "Portland - Seattle",
    demand: 100,
    active: true,
    aircraftId: null,        // Assigned aircraft ID (null = unassigned)
    autoDispatch: false,     // Auto-launch when ready
    status: 'unassigned',    // unassigned, ready, in_flight, turnaround, delayed, crew_issue
    lastFlightId: null,      // ID of current/last flight on this route
    lastCompletedAt: null,   // When last flight completed
    totalFlights: 0,         // Lifetime flights on this route
    totalRevenue: 0,         // Lifetime revenue
  },
  {
    id: "KPDX-KBOI",
    origin: "KPDX",
    destination: "KBOI",
    name: "Portland - Boise",
    demand: 85,
    active: true,
    aircraftId: null,
    autoDispatch: false,
    status: 'unassigned',
    lastFlightId: null,
    lastCompletedAt: null,
    totalFlights: 0,
    totalRevenue: 0,
  },
  {
    id: "KPDX-KRDM",
    origin: "KPDX",
    destination: "KRDM",
    name: "Portland - Redmond",
    demand: 60,
    active: true,
    aircraftId: null,
    autoDispatch: false,
    status: 'unassigned',
    lastFlightId: null,
    lastCompletedAt: null,
    totalFlights: 0,
    totalRevenue: 0,
  },
  {
    id: "KPDX-KSMF",
    origin: "KPDX",
    destination: "KSMF",
    name: "Portland - Sacramento",
    demand: 90,
    active: true,
    aircraftId: null,
    autoDispatch: false,
    status: 'unassigned',
    lastFlightId: null,
    lastCompletedAt: null,
    totalFlights: 0,
    totalRevenue: 0,
  },
];

// Get the start of today in UTC for new players
const getDefaultGameTime = () => {
  const now = new Date();
  // Start at 6 AM local time today (typical airline ops start time)
  now.setHours(6, 0, 0, 0);
  return now.toISOString();
};

const DEFAULT_WORLD_STATE = {
  // ============================================================================
  // SAVE SAFETY - Prevents saving default state
  // ============================================================================
  isInitialized: false,   // CRITICAL: Only true after real save loads

  // ============================================================================
  // TIME - New players start at real time (6 AM today)
  // ============================================================================
  gameTime: getDefaultGameTime(),
  gameStartDate: getDefaultGameTime(), // When the player first started (used for dayNumber calculation)
  gameSpeed: 1,
  isPaused: false,
  gameMode: 'simple',     // 'simple' = one-click ops, 'simulation' = full preflight
  tierBypass: false,      // Cheat: bypass tier restrictions for routes
  airportsGated: true,    // Ship default: airports gated by tier (false = all airports unlocked)
  dayNumber: 1,
  lastScheduleGeneratedDay: 0,
  lastMonthProcessed: 0,
  pendingEndOfDay: false,    // True when 22:00 reached, triggers modal
  endOfDayHandled: false,    // True after modal dismissed, prevents re-trigger same day

  // ============================================================================
  // AIRLINE
  // ============================================================================
  airline: {
    name: "Pacific Wings",
    callsign: "PACWING",
    code: "PW",
    hub: DEFAULT_HUB,
    cash: 40000000, // $40M starting cash (per GDD)
    colors: { primary: "#00ffbf", accent: "#0088ff" },
    logo: null,
    livery: null,
  },

  // ============================================================================
  // PROGRESSION
  // ============================================================================
  tier: 1,
  tierProgress: {
    consecutiveCrisisDays: 0,
    demotionWarning: false,
  },
  aocTier: 1, // AOC Tier (flight-count based)

  // Fleet unlock tiers (Simulation mode only)
  // Players start with regional, unlock larger aircraft via network growth
  fleetUnlocks: {
    regional: true,      // Always unlocked
    narrowbody: false,   // Major slots, 1500nm route, or 7000 rep
    widebody: false,     // International slots, 3500nm route, or 12000 rep
    heavy: false,        // 15000 rep, 3+ international airports, or 50 widebody flights
  },

  // ============================================================================
  // REPUTATION (from ReputationEngine)
  // ============================================================================
  reputation: createReputationState(),

  // ============================================================================
  // FLEET, CREW, ROUTES
  // ============================================================================
  aircraft: STARTER_FLEET,
  crew: STARTER_CREW,
  routes: STARTER_ROUTES,

  // ============================================================================
  // HUBS (Simple mode only - purchased hubs for network expansion)
  // ============================================================================
  ownedHubs: ['KPDX'], // Player always starts with KPDX as their hub

  // ============================================================================
  // LEASES (from LeaseManager)
  // ============================================================================
  leases: [],
  lastBillingDate: null,

  // ============================================================================
  // OPERATIONS
  // ============================================================================
  flights: [],
  activeFlightIds: [],
  todaySchedule: [],
  scheduledFlights: [],  // Scheduled departures (see ScheduleFlightModal)

  // ============================================================================
  // WEATHER (from WeatherService.js)
  // Real-time weather data for airports - affects delays and ground stops
  // ============================================================================
  airportWeather: {},
  /*
    airportWeather: {
      [icao]: {
        data: { windSpeed, visibility, precipitationIntensity, weatherCode, ... },
        impact: { canDepart, delayMinutes, reason, severity },
        lastUpdated: ISO timestamp
      }
    }
  */

  // ============================================================================
  // PURCHASABLE SYSTEMS (per GDD)
  // ============================================================================
  systems: {
    crewWise: false,        // $18M - predicts crew illegality
    webLogic: false,        // $22M - eliminates deadheading
    crewRail: false,        // Uses retired E175s as shuttles
    hrd: false,             // Historical Route Data
    yieldMaster: false,     // Dynamic pricing
  },
  systemsInstalledDays: {
    crewWise: 0,
    webLogic: 0,
  },

  // ============================================================================
  // EOS (Elite Operator Status) - From EOSSystem.js
  // Binary elite certification earned through 7 consecutive days of Tier 5 excellence
  // Unlocks A380-triggered rare aircraft rolls
  // ============================================================================
  eos: createEOSState(),

  // ============================================================================
  // RELATIONS (from RelationsEngine.js)
  // Airport and competitor relationships affecting negotiations and pricing
  // ============================================================================
  relations: createRelationsState(),

  // ============================================================================
  // CONSULTANTS (from ConsultantsSystem.js)
  // Negotiators, lawyers, fixers - provide ongoing bonuses
  // ============================================================================
  consultants: createConsultantState(),

  // ============================================================================
  // INBOX (Protocol 13 - The Inbox)
  // Central communication hub for all game intel
  // Receives messages from: specialists, FAA, airports, competitors, system alerts
  // ============================================================================
  inbox: {
    messages: [
      {
        id: 'welcome-1',
        fromKey: 'inbox.welcome.kpdx.from',
        fromTitleKey: 'inbox.welcome.kpdx.fromTitle',
        subjectKey: 'inbox.welcome.kpdx.subject',
        bodyKey: 'inbox.welcome.kpdx.body',
        date: getDefaultGameTime(),
        read: false,
        type: 'airport',
        reliability: null,
        stars: null,
        metadata: { airportCode: 'KPDX' },
      },
      {
        id: 'welcome-2',
        fromKey: 'inbox.welcome.skyreach.from',
        fromTitleKey: 'inbox.welcome.skyreach.fromTitle',
        subjectKey: 'inbox.welcome.skyreach.subject',
        bodyKey: 'inbox.welcome.skyreach.body',
        date: getDefaultGameTime(),
        read: false,
        type: 'system',
        reliability: null,
        stars: null,
        metadata: { category: 'advertising' },
      },
      {
        id: 'welcome-3',
        fromKey: 'inbox.welcome.alaska.from',
        fromTitleKey: 'inbox.welcome.alaska.fromTitle',
        subjectKey: 'inbox.welcome.alaska.subject',
        bodyKey: 'inbox.welcome.alaska.body',
        date: getDefaultGameTime(),
        read: false,
        type: 'competitor',
        reliability: null,
        stars: null,
        metadata: { competitor: 'Alaska Airlines' },
      },
      {
        id: 'welcome-4',
        fromKey: 'inbox.welcome.faa.from',
        fromTitleKey: 'inbox.welcome.faa.fromTitle',
        subjectKey: 'inbox.welcome.faa.subject',
        bodyKey: 'inbox.welcome.faa.body',
        date: getDefaultGameTime(),
        read: false,
        type: 'faa',
        reliability: null,
        stars: null,
        metadata: {},
      },
    ],
    /*
      Each message:
      {
        id: string,           // Unique ID
        from: string,         // Sender name (e.g., "Marcus Webb", "FAA", "SYSTEM")
        fromTitle: string,    // Optional title (e.g., "Master Insider", "Regional Director")
        subject: string,      // Email subject line
        body: string,         // Email body text
        date: ISO string,     // When received (game time)
        read: boolean,        // Has player read this?
        type: 'intel' | 'system' | 'faa' | 'airport' | 'competitor',
        reliability: number | null,  // 0-100 for intel, null for system messages
        stars: number | null,        // 1-5 specialist rating, null for non-intel
        metadata: {}          // Optional extra data (airportCode, specialistId, etc.)
      }
    */
  },

  // ============================================================================
  // INNER CIRCLE (Protocol 11 & 12 - Strategic Specialists)
  // One-time contractors for hub bid intel
  // ============================================================================
  innerCircle: {
    unlocked: true, // TODO: Gate behind hub expansion later
    hired: [],
    /*
      Each hired specialist:
      {
        id: string,           // Unique specialist ID
        name: string,         // Generated name
        title: string,        // "Master Accountant", "Master Insider", etc.
        fee: number,          // One-time fee paid
        stars: number,        // 1-5 star rating
        reliability: [min, max], // e.g., [40, 50] for 40-50%
        risk: 'none' | 'medium' | 'high' | 'extreme',
        hiredAt: ISO string,  // When hired
        hubBidId: string | null, // Which hub bid they're working on (future)
      }
    */
  },

  // ============================================================================
  // CREW MARKET (from CrewMarketSystem.js)
  // Pilots with RPG-style stats: experience, reliability, fatigue tolerance
  // ============================================================================
  crewMarket: createCrewState(),

  // ============================================================================
  // ADVERTISING (from AdvertisingSystem.js)
  // Marketing campaigns that boost demand and relations
  // ============================================================================
  advertising: createAdvertisingState(),

  // ============================================================================
  // REBRANDING (from RebrandingSystem.js)
  // $2M to reset negative relations, once per year
  // ============================================================================
  rebranding: createRebrandingState(),

  // ============================================================================
  // TECH SYSTEMS (from TechSystems.js)
  // CrewWise, WebLogic, YieldMaster, MaintenancePro
  // ============================================================================
  tech: createTechState(),

  // ============================================================================
  // SUPPORT CREW (from SupportCrewSystem.js)
  // Mechanics, fleet operators, engineers - affect maintenance & turnaround
  // ============================================================================
  supportCrew: createSupportCrewState(),

  // ============================================================================
  // FUEL SYSTEM (from FuelSystem.js) - Simulation Mode Only
  // Spot market, supplier contracts, hedging
  // ============================================================================
  fuel: createFuelState(),

  // ============================================================================
  // FUEL SYSTEM - Simple Mode (Strategic Fuel Trading)
  // Dynamic spot price, market trends, price history
  // ============================================================================
  fuelSpotPrice: 3.00,          // Current spot price $/gallon
  fuelPriceHistory: [],         // Last 30 days of prices [{date, price}]
  fuelMarketTrend: {
    direction: 'stable',        // 'inflationary', 'deflationary', 'stable'
    strength: 0,                // Daily push toward trend (0.01-0.03 for active trends)
    daysRemaining: 30,          // How long trend lasts
    startPrice: 3.00,           // Price when trend started
  },
  fuelContract: null,           // Active fuel contract { lockedPrice, startDay, endDay, minimumGallons, purchasedGallons, status }
  fuelHedge: null,              // Active fuel hedge { capPrice, premiumPaid, startDay, endDay, status }
  maxFuelCapacity: 1000000,     // Maximum fuel storage in gallons (reduced from 2M)
  fuelReserves: 1000000,        // Current fuel reserves - start full

  // ============================================================================
  // PROGRESSION
  // Player tier, unlock tracking, achievements
  // ============================================================================
  progression: {
    currentTier: 1,
    unlockedTiers: [1],
    consecutiveProfitDays: 0,
    totalFlightsCompleted: 0,
    lifetimeRevenue: 0,
    currentOTP: 100,
    tierUnlockHistory: [{ tier: 1, unlockedAt: Date.now(), gameDay: 1 }],
    hasInternationalCert: false,
    internationalCertProgress: 0,
    achievements: [],
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================
  stats: {
    totalFlights: 0,
    completedFlights: 0,
    onTimeFlights: 0,
    delayedFlights: 0,
    cancelledFlights: 0,
    totalRevenue: 0,
    totalCosts: 0,
    totalProfit: 0,
    passengersMoved: 0,
    // Daily tracking
    dailyFlights: 0,
    dailyDepartures: 0,
    dailyDelays: 0,
    dailyCancellations: 0,
    dailyRevenue: 0,
    dailyCosts: 0,
    // Monthly tracking
    monthlyFlights: 0,
    monthlyCascades: 0,
    monthlyRevenue: 0,
    monthlyCosts: 0,
  },

  // ============================================================================
  // FINANCIAL HISTORY (for profit chart)
  // ============================================================================
  financialHistory: [],

  // ============================================================================
  // EVENTS & NEWS
  // ============================================================================
  events: [],
  newsFeed: [],

  // ============================================================================
  // WEATHER (managed by GameLoopEngine)
  // ============================================================================
  weather: {
    cells: [],
    airportConditions: {},
    lastUpdate: null,
  },

  // ============================================================================
  // MAINTENANCE
  // ============================================================================
  maintenance: {
    lastAChecks: {},
    lastCChecks: {},
    violations: 0,
  },

  // ============================================================================
  // TRANSACTIONS (for financial history)
  // ============================================================================
  transactions: [],

  // ============================================================================
  // IAP TRACKING (for OAG leaderboard exclusion)
  // ============================================================================
  iap: {
    totalGranted: 0,     // Total cash from IAP purchases
    purchaseCount: 0,    // Number of IAP transactions
    lastPurchase: null,  // ISO timestamp of last purchase
  },

  // ============================================================================
  // SLOT NEGOTIATIONS
  // ============================================================================
  negotiation: null, // Active negotiation state when triggered
  /*
    When active:
    {
      phase: 'legal' | 'bidding' | 'result',
      airportCode: 'KSMF',
      airportName: 'Sacramento International',
      triggeredAt: ISO string,
      availableSlots: [...],
      competitors: [...],
      playerBids: [...],       // Set after bidding
      competitorBids: [...],   // Set after bidding
      results: [...],          // Set after determination
      reputationChanges: [...],
      relationshipChanges: [...],
      totalCost: 0,
    }
  */
  // Player starts with slots at their hub + starter route airports
  // This is their initial operating certificate / startup deal
  // Option 3: Hub + 2-3 nearby airports - realistic startup negotiation already complete
  // Note: acquiredAt uses getDefaultGameTime() for real-time start
  slots: {
    // Hub - 5 slots, no fee (included in startup deal)
    KPDX: [
      { slotId: "KPDX-A", acquiredAt: getDefaultGameTime(), monthlyFee: 0, expiresAt: null, isHubSlot: true, departureTime: "06:13", status: "available" },
      { slotId: "KPDX-B", acquiredAt: getDefaultGameTime(), monthlyFee: 0, expiresAt: null, isHubSlot: true, departureTime: "08:26", status: "available" },
      { slotId: "KPDX-C", acquiredAt: getDefaultGameTime(), monthlyFee: 0, expiresAt: null, isHubSlot: true, departureTime: "11:34", status: "available" },
      { slotId: "KPDX-D", acquiredAt: getDefaultGameTime(), monthlyFee: 0, expiresAt: null, isHubSlot: true, departureTime: "14:47", status: "available" },
      { slotId: "KPDX-E", acquiredAt: getDefaultGameTime(), monthlyFee: 0, expiresAt: null, isHubSlot: true, departureTime: "17:53", status: "available" },
    ],
    // Starter network - 2 slots each at major destinations, reduced fees (startup deal)
    KSEA: [
      { slotId: "KSEA-A", acquiredAt: getDefaultGameTime(), monthlyFee: 15000, expiresAt: null, isStarterSlot: true, departureTime: "07:17", status: "available" },
      { slotId: "KSEA-B", acquiredAt: getDefaultGameTime(), monthlyFee: 15000, expiresAt: null, isStarterSlot: true, departureTime: "16:38", status: "available" },
    ],
    KBOI: [
      { slotId: "KBOI-A", acquiredAt: getDefaultGameTime(), monthlyFee: 10000, expiresAt: null, isStarterSlot: true, departureTime: "08:23", status: "available" },
      { slotId: "KBOI-B", acquiredAt: getDefaultGameTime(), monthlyFee: 10000, expiresAt: null, isStarterSlot: true, departureTime: "15:41", status: "available" },
    ],
    // Smaller destination - 1 slot
    KRDM: [
      { slotId: "KRDM-A", acquiredAt: getDefaultGameTime(), monthlyFee: 5000, expiresAt: null, isStarterSlot: true, departureTime: "12:07", status: "available" },
    ],
  }, // airportCode -> [{ slotId, acquiredAt, monthlyFee, expiresAt, departureTime, status }]
  negotiationHistory: [], // Past negotiations
  legal: null, // Legal firm state: { currentFirm, relationships, ... }

  // ============================================================================
  // AIRPORT UNLOCK SYSTEM
  // ============================================================================
  // Airports unlock via: reputation tiers, HRD intel, invitations, competitor withdrawal
  unlockedAirports: {
    // Hub is always unlocked and has slots
    KPDX: { unlockReason: "hub", unlockedAt: getDefaultGameTime() },
    // Tier 1 starter routes - these need negotiations but are available from start
    KSEA: { unlockReason: "tier1_starter", unlockedAt: getDefaultGameTime() },
    KBOI: { unlockReason: "tier1_starter", unlockedAt: getDefaultGameTime() },
    KRDM: { unlockReason: "tier1_starter", unlockedAt: getDefaultGameTime() },
    KSMF: { unlockReason: "tier1_starter", unlockedAt: getDefaultGameTime() },
  },
  // HRD (Historical Route Data) purchases - unlocks market intel + negotiation
  hrdPurchases: [], // [{ airportCode, purchasedAt, cost }]

  // ============================================================================
  // WINGS CURRENCY (from WingsSystem.js)
  // Secondary currency earned through flights, spent on Smart Deploy
  // ============================================================================
  wings: {
    balance: 20,              // Starting Wings for new players
    lifetimeEarned: 20,       // Total earned (for stats)
    lifetimeSpent: 0,         // Total spent (for stats)
    flightsSinceLastWing: 0,  // Counter toward next earned Wing
  },

  // ============================================================================
  // ROUTE DEMAND (from RouteDemandSystem.js)
  // Each route depletes with usage and recovers daily. Affects yield.
  // ============================================================================
  routeDemand: {},  // Key: "AAA-BBB" (alphabetical), Value: 0-100

  // ============================================================================
  // BUFFS (Temporary bonuses from achievements like Mythic roll)
  // Expiration uses real time (Date.now()) for persistence across sessions
  // ============================================================================
  buffs: {
    halfPriceFuel: {
      active: false,
      expiresAt: null,  // Real timestamp (Date.now() + duration)
      grantedAt: null,
    },
    doubleRevenue: {
      active: false,
      expiresAt: null,
      grantedAt: null,
    },
  },

  // ============================================================================
  // MYTHIC ROLL TRACKING (for leaderboard display)
  // ============================================================================
  mythicRoll: {
    hasMythic: false,
    steamName: null,
    rolledAt: null,  // Real timestamp
  },
};

// ============================================================================
// CONTEXT
// ============================================================================

const WorldStateContext = createContext(null);

export function useWorldState() {
  const context = useContext(WorldStateContext);
  if (!context) {
    throw new Error("useWorldState must be used within a WorldStateProvider");
  }
  return context;
}

// ============================================================================
// PROVIDER
// ============================================================================

export function WorldStateProvider({ children }) {
  // Steam user info (loaded on mount)
  const [steamUser, setSteamUser] = useState({ steamId: null, displayName: null });

  const [worldState, setWorldState] = useState(() => {
    // Check for wizard-created airline in localStorage
    const savedPlayerAirline = localStorage.getItem('playerAirline');
    if (savedPlayerAirline) {
      try {
        const wizardAirline = JSON.parse(savedPlayerAirline);
        // Merge wizard data into defaults
        return {
          ...DEFAULT_WORLD_STATE,
          airline: {
            ...DEFAULT_WORLD_STATE.airline,
            name: wizardAirline.name || DEFAULT_WORLD_STATE.airline.name,
            code: wizardAirline.iataCode || DEFAULT_WORLD_STATE.airline.code,
            hub: wizardAirline.hub || DEFAULT_WORLD_STATE.airline.hub,
            colors: wizardAirline.colors || DEFAULT_WORLD_STATE.airline.colors,
            logo: wizardAirline.logo || null,
            livery: wizardAirline.livery || null,
          },
        };
      } catch (e) {
        console.warn("[WorldState] Failed to parse playerAirline:", e);
      }
    }
    // Start with defaults - will be updated from file save
    return { ...DEFAULT_WORLD_STATE };
  });

  const [saveLoaded, setSaveLoaded] = useState(false);
  const [actualSaveLoaded, setActualSaveLoaded] = useState(false); // Track when real save (not default) is loaded
  const [continuedFromMenu, setContinuedFromMenu] = useState(false); // Track if user clicked Continue
  const [hasFullyLoaded, setHasFullyLoaded] = useState(false); // CRITICAL: Prevent saves until fully loaded
  const hasLoadedFromFileRef = useRef(false); // Secondary guard
  const lastProcessedHour = useRef(-1);

  // Load Supabase user info (for display purposes — auth handled by AuthContext)
  useEffect(() => {
    getSteamUser().then(user => {
      setSteamUser(user);
      debug("[WorldState] User:", user.displayName || "Not available (web)");
    });
  }, []);

  // Load game data from file - ONLY ONCE per session
  useEffect(() => {
    if (saveLoaded) return;
    if (hasLoadedFromFileRef.current) return; // Ref-based guard
    hasLoadedFromFileRef.current = true;

    async function loadFromFile() {
      try {
        debug("[WorldState] Loading from save file...");

        // Check if this is a new game - should start fresh
        const isNewGame = localStorage.getItem('isNewGame') === 'true';
        if (isNewGame) {
          debug("[WorldState] New game detected - starting fresh");
          localStorage.removeItem('isNewGame');
          setWorldState(prev => ({ ...prev, isInitialized: true }));
          setSaveLoaded(true);
          setHasFullyLoaded(true);
          return; // Use default state
        }

        // Load from file
        const result = await loadGame();

        if (!result.success) {
          console.error("[WorldState] Failed to load save:", result.error);
          setSaveLoaded(true);
          return;
        }

        if (!result.exists || !result.data) {
          debug("[WorldState] No save file found - starting fresh");
          setWorldState(prev => ({ ...prev, isInitialized: true }));
          setSaveLoaded(true);
          setHasFullyLoaded(true);
          return;
        }

        const savedState = result.data;
        debug("[WorldState] Save file loaded, metadata:", result.metadata);

        // Check if loaded game time is past 22:00 - if so, mark end of day as handled
        const loadedGameTime = savedState.gameTime || getDefaultGameTime();
        const loadedHour = new Date(loadedGameTime).getHours();
        const shouldSkipEndOfDay = loadedHour >= 22;

        // Load gameMode from localStorage (persisted during mode selection)
        const savedGameMode = localStorage.getItem('gameMode');
        const gameMode = (savedGameMode === 'simple' || savedGameMode === 'simulation')
          ? savedGameMode
          : (savedState.gameMode || 'simple');

        // Merge saved state with defaults (in case save is missing new fields)
        setWorldState(prev => ({
          ...DEFAULT_WORLD_STATE,
          ...savedState,
          // CRITICAL: Mark as initialized - this is a real save
          isInitialized: true,
          // Always reset speed to 1x on load (don't persist high speeds)
          gameSpeed: 1,
          isPaused: false,
          gameMode,
          // Prevent end of day modal from showing if loading into late hours
          pendingEndOfDay: false,
          endOfDayHandled: shouldSkipEndOfDay,
          // Preserve EOS with recovery logic
          eos: (() => {
            const savedEOS = savedState.eos || {};
            const localEOS = prev.eos || {};

            // Check emergency backup - use function to avoid TDZ issues with minification
            // (let reassignment inside try/catch in IIFE can cause "Cannot access X before initialization")
            const getEmergencyEOS = () => {
              try {
                const emergency = localStorage.getItem('airlineOpsEOS_emergency');
                if (emergency) {
                  const parsed = JSON.parse(emergency);
                  localStorage.removeItem('airlineOpsEOS_emergency');
                  return {
                    consecutiveDays: parsed.consecutiveDays || 0,
                    hasEOS: parsed.hasEOS || false,
                  };
                }
              } catch (e) { /* ignore */ }
              return { consecutiveDays: 0, hasEOS: false };
            };

            const emergencyEOS = getEmergencyEOS();

            // Use highest streak (protect against data loss)
            const sources = [
              { name: 'file', ...savedEOS },
              { name: 'local', ...localEOS },
              { name: 'emergency', ...emergencyEOS },
            ];
            const best = sources.reduce((a, b) =>
              (b.consecutiveDays > a.consecutiveDays || b.hasEOS) ? b : a
            );

            return {
              ...createEOSState(),
              ...savedEOS,
              consecutiveDays: Math.max(best.consecutiveDays, savedEOS.consecutiveDays || 0),
              hasEOS: best.hasEOS || savedEOS.hasEOS || false,
            };
          })(),
        }));

        // Compensation will be checked in separate effect after Steam user loads

        setSaveLoaded(true);
        setActualSaveLoaded(true); // Mark that we loaded a real save, not defaults
        setHasFullyLoaded(true); // CRITICAL: Now safe to allow saves
        debug("[WorldState] Loaded from save file:", {
          gameMode,
          aircraft: savedState.aircraft?.length || 0,
          crew: savedState.crew?.length || 0,
          flights: savedState.flights?.length || 0,
          dayNumber: savedState.dayNumber,
        });

      } catch (err) {
        console.error("[WorldState] Failed to load from file:", err);
        setSaveLoaded(true);
      }
    }

    loadFromFile();
  }, [saveLoaded]);

  // AUTO-FIX: Convert "delivery" aircraft to "available" in Simple mode
  // Delivery timers aren't implemented, so aircraft get stuck in delivery status
  useEffect(() => {
    if (!worldState.aircraft || worldState.aircraft.length === 0) return;
    const isSimpleMode = worldState.gameMode === 'simple' || worldState.gameMode === 'arcade';

    // Check if any aircraft are stuck in delivery
    const deliveryAircraft = worldState.aircraft.filter(a => a.status === 'delivery');
    if (deliveryAircraft.length > 0 && isSimpleMode) {
      debug(`[WorldState] 🔧 Auto-fixing ${deliveryAircraft.length} aircraft stuck in 'delivery' status`);
      setWorldState(prev => ({
        ...prev,
        aircraft: prev.aircraft.map(a =>
          a.status === 'delivery' ? { ...a, status: 'available' } : a
        ),
      }));
    }
  }, [worldState.aircraft?.length, worldState.gameMode]);

  // ============================================================================
  // COMPENSATION CHECK - After BOTH Steam user AND save are loaded
  // ============================================================================
  // Compensation system removed for web version (Steam-specific)

  // ============================================================================
  // SAVE TO FILE - Debounced auto-save (every 2 seconds of changes)
  // ============================================================================
  useEffect(() => {
    if (!hasFullyLoaded) return; // CRITICAL: Don't save until FULLY loaded

    // DOUBLE CHECK: Never save default state
    if (worldState.airline?.cash === 40000000 && worldState.dayNumber === 1) {
      console.error('[WorldState] BLOCKED: Refusing to autosave default state!');
      return;
    }

    const timeout = setTimeout(async () => {
      await saveGame(worldState);
      // Phase 5: Supabase leaderboard submission will go here
    }, 2000);
    return () => clearTimeout(timeout);
  }, [worldState, hasFullyLoaded]); // Changed to hasFullyLoaded

  // ============================================================================
  // SAVE ON PAUSE - Immediate save when game pauses
  // ============================================================================
  useEffect(() => {
    if (!saveLoaded) return;
    if (!worldState.isPaused) return;

    const saveOnPause = async () => {
      debug('[WorldState] Saving on pause...');
      await saveGame(worldState);
    };
    saveOnPause();
  }, [worldState.isPaused, saveLoaded]);

  // ============================================================================
  // EMERGENCY SAVE - beforeunload (tab close / browser close)
  // ============================================================================
  useEffect(() => {
    if (!saveLoaded) return;

    const syncOnUnload = () => {
      const eos = worldState.eos || {};
      debug('[WorldState] Emergency save on unload (EOS:', eos.consecutiveDays || 0, 'days)');

      // Save to localStorage as emergency backup (synchronous, always works)
      try {
        localStorage.setItem("airlineOpsWorldState", JSON.stringify(worldState));
        localStorage.setItem("airlineOpsEOS_emergency", JSON.stringify({
          consecutiveDays: eos.consecutiveDays || 0,
          hasEOS: eos.hasEOS || false,
          lockoutUntil: eos.lockoutUntil || null,
          savedAt: new Date().toISOString(),
        }));
      } catch (e) {
        console.warn('[WorldState] Emergency localStorage save failed:', e);
      }

      // Note: File save via IPC is async and may not complete during unload
      // localStorage backup ensures data isn't lost
    };

    window.addEventListener('beforeunload', syncOnUnload);
    return () => window.removeEventListener('beforeunload', syncOnUnload);
  }, [saveLoaded, worldState]);

  // ============================================================================
  // EMERGENCY SAVE - visibilitychange (tab switch / minimize)
  // Also handles recovery from white screen after computer lock
  // ============================================================================
  useEffect(() => {
    if (!saveLoaded) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        // Save when hiding
        const eos = worldState.eos || {};
        debug('[WorldState] Saving on tab hide (EOS:', eos.consecutiveDays || 0, 'days)');
        await saveGame(worldState);
      } else if (document.visibilityState === 'visible') {
        // Recovery check when becoming visible again (after computer lock/sleep)
        // Force a small state update to trigger re-render of any broken components
        debug('[WorldState] Tab visible - triggering recovery re-render');
        setWorldState(prev => ({ ...prev, _lastVisibleAt: Date.now() }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [saveLoaded, worldState]);

  // ============================================================================
  // FLEET PROGRESSION CHECK (Simulation mode)
  // Automatically check for fleet tier unlocks when relevant state changes
  // ============================================================================
  useEffect(() => {
    // Only in simulation mode
    if (worldState?.gameMode !== 'simulation') return;

    // Get current state for comparison
    const unlocks = worldState?.fleetUnlocks || {};

    // Skip if all already unlocked
    if (unlocks.narrowbody && unlocks.widebody && unlocks.heavy) return;

    // Check for unlocks
    const { unlocks: newUnlocks, newUnlocks: justUnlocked } = checkFleetUnlocks(worldState);

    // Update if anything new unlocked
    if (justUnlocked.length > 0) {
      setWorldState((prev) => ({
        ...prev,
        fleetUnlocks: newUnlocks,
        pendingFleetUnlock: justUnlocked[0],
      }));
    }
  // Re-check when slots, routes, or reputation changes
  }, [
    worldState?.gameMode,
    worldState?.slots,
    worldState?.routes,
    worldState?.reputation?.currentScore,
    worldState?.fleetUnlocks?.narrowbody,
    worldState?.fleetUnlocks?.widebody,
    worldState?.fleetUnlocks?.heavy,
  ]);

  // ============================================================================
  // HUB INVITE EMAIL NOTIFICATION
  // Send email when new hub invites are added to pendingHubInvites
  // ============================================================================
  const prevPendingHubInvitesRef = useRef([]);
  useEffect(() => {
    const currentInvites = worldState?.pendingHubInvites || [];
    const prevInvites = prevPendingHubInvitesRef.current;

    // Find newly added hubs
    const newHubs = currentInvites.filter(hub => !prevInvites.includes(hub));

    // Send email for each new hub invite
    newHubs.forEach(hub => {
      const hubName = AIRPORTS[hub]?.name || FULL_AIRPORTS[hub]?.name || hub;
      const hubCity = AIRPORTS[hub]?.city || FULL_AIRPORTS[hub]?.city || '';

      // Create email with action button (uses translation keys with interpolation)
      const emailMessage = {
        id: `hub-invite-${hub}-${Date.now()}`,
        from: hubName,  // Fallback - real name shown if translation fails
        fromTitleKey: 'inbox.hubInvite.fromTitle',
        subjectKey: 'inbox.hubInvite.subject',
        bodyKey: 'inbox.hubInvite.body',
        // Store interpolation values for translation
        interpolation: {
          hub,
          hubName,
          hubCity: hubCity || '',
          location: hubCity ? `${hubName} in ${hubCity}` : hubName,
        },
        date: worldState?.gameTime || new Date().toISOString(),
        type: 'airport',
        read: false,
        actions: [
          {
            type: 'open_hub_auction',
            hub: hub,
            labelKey: 'inbox.hubInvite.enterAuction',
            variant: 'primary',
          },
        ],
      };

      setWorldState(prev => ({
        ...prev,
        inbox: {
          ...prev.inbox,
          messages: [emailMessage, ...prev.inbox.messages],
        },
      }));

      debug(`[WorldState] Sent hub invite email for ${hub}`);
    });

    // Update ref
    prevPendingHubInvitesRef.current = currentInvites;
  }, [worldState?.pendingHubInvites, worldState?.gameTime]);

  // ============================================================================
  // CORE UPDATE FUNCTIONS
  // ============================================================================

  const updateWorldState = useCallback((updates) => {
    setWorldState((prev) => {
      if (typeof updates === "function") {
        return updates(prev);
      }
      return { ...prev, ...updates };
    });
  }, []);

  const resetWorldState = useCallback(() => {
    localStorage.removeItem("airlineOpsWorldState");
    setWorldState({ ...DEFAULT_WORLD_STATE });
  }, []);

  // ============================================================================
  // TIME CONTROLS
  // ============================================================================

  const setGameSpeed = useCallback((speed) => {
    setWorldState((prev) => {
      // Speed caps: 4x always available, 12x unlocked with EOS
      const unlocks = prev.unlocks || {};
      let maxSpeed = 4; // 4x always available from start
      if (unlocks.speed12x) maxSpeed = 12; // 12x unlocked with EOS
      const cappedSpeed = Math.min(speed, maxSpeed);
      return { ...prev, gameSpeed: cappedSpeed, isPaused: false };
    });
  }, []);

  const togglePause = useCallback(() => {
    setWorldState((prev) => ({ ...prev, isPaused: !prev.isPaused }));
  }, []);

  // ============================================================================
  // CASH MANAGEMENT
  // ============================================================================

  const updateCash = useCallback((amount) => {
    setWorldState((prev) => ({
      ...prev,
      airline: {
        ...prev.airline,
        cash: prev.airline.cash + amount,
      },
    }));
  }, []);

  const addTransaction = useCallback((transaction) => {
    setWorldState((prev) => ({
      ...prev,
      transactions: [
        ...prev.transactions.slice(-99),
        { ...transaction, id: `TXN-${Date.now()}`, timestamp: new Date().toISOString() },
      ],
    }));
  }, []);

  // ============================================================================
  // FLEET MANAGEMENT
  // ============================================================================

  const addAircraft = useCallback((aircraft) => {
    setWorldState((prev) => {
      // Ensure aircraft has a location (defaults to hub)
      const hub = prev.airline?.hub || "KPDX";
      const aircraftWithLocation = {
        ...aircraft,
        location: aircraft.location || hub,
      };
      return {
        ...prev,
        aircraft: [...prev.aircraft, aircraftWithLocation],
      };
    });
  }, []);

  const updateAircraft = useCallback((aircraftId, updates) => {
    setWorldState((prev) => ({
      ...prev,
      aircraft: prev.aircraft.map((a) =>
        a.id === aircraftId ? { ...a, ...updates } : a
      ),
    }));
  }, []);

  const removeAircraft = useCallback((aircraftId) => {
    setWorldState((prev) => ({
      ...prev,
      aircraft: prev.aircraft.filter((a) => a.id !== aircraftId),
    }));
  }, []);

  // ============================================================================
  // LEASE MANAGEMENT
  // ============================================================================

  const addLease = useCallback((lease) => {
    setWorldState((prev) => ({
      ...prev,
      leases: [...prev.leases, lease],
    }));
  }, []);

  const updateLease = useCallback((leaseId, updates) => {
    setWorldState((prev) => ({
      ...prev,
      leases: prev.leases.map((l) =>
        l.id === leaseId ? { ...l, ...updates } : l
      ),
    }));
  }, []);

  // ============================================================================
  // CREW MANAGEMENT
  // ============================================================================

  const addCrew = useCallback((crewMembers) => {
    setWorldState((prev) => ({
      ...prev,
      crew: [...prev.crew, ...(Array.isArray(crewMembers) ? crewMembers : [crewMembers])],
    }));
  }, []);

  const updateCrew = useCallback((crewId, updates) => {
    setWorldState((prev) => ({
      ...prev,
      crew: prev.crew.map((c) => (c.id === crewId ? { ...c, ...updates } : c)),
    }));
  }, []);

  // ============================================================================
  // RELATIONS MANAGEMENT
  // ============================================================================

  const updateRelations = useCallback((updates) => {
    setWorldState((prev) => ({
      ...prev,
      relations: typeof updates === 'function' ? updates(prev.relations) : { ...prev.relations, ...updates }
    }));
  }, []);

  // ============================================================================
  // FLIGHT MANAGEMENT
  // ============================================================================

  const createFlight = useCallback((flightData) => {
    const { origin, destination } = flightData;
    const hub = worldState?.airline?.hub || "KPDX";
    const slots = worldState?.slots || {};
    const gameMode = worldState?.gameMode || 'simple';

    // =================================================================
    // BUG-3 FIX: Validate airports exist before creating flight
    // Prevents NaN poisoning from invalid airport codes
    // =================================================================
    const origAirport = AIRPORTS[origin] || FULL_AIRPORTS[origin] || GLOBAL_AIRPORTS[origin];
    const destAirport = AIRPORTS[destination] || FULL_AIRPORTS[destination] || GLOBAL_AIRPORTS[destination];

    if (!origAirport) {
      console.error(`[FLIGHT BLOCKED] Invalid origin airport: ${origin}`);
      return { error: true, message: `Invalid origin airport: ${origin}. Airport not found in database.` };
    }
    if (!destAirport) {
      console.error(`[FLIGHT BLOCKED] Invalid destination airport: ${destination}`);
      return { error: true, message: `Invalid destination airport: ${destination}. Airport not found in database.` };
    }

    // Validate distance can be calculated (will catch coordinate issues)
    const routeDistance = calculateDistance(origin, destination);
    if (!Number.isFinite(routeDistance)) {
      console.error(`[FLIGHT BLOCKED] Cannot calculate distance for ${origin} -> ${destination}`);
      return { error: true, message: `Cannot create flight: unable to calculate route distance for ${origin} -> ${destination}.` };
    }

    // SLOT VALIDATION: Skip in Simple mode - fly anywhere freely
    // In Simulation mode, check if player has slots at both airports
    // Note: 'arcade' is legacy name for 'simple' - accept both
    const isSimpleMode = gameMode === 'simple' || gameMode === 'arcade';
    if (!isSimpleMode) {
      const hasOriginSlot = origin === hub || (slots[origin]?.length > 0);
      const hasDestSlot = destination === hub || (slots[destination]?.length > 0);

      if (!hasOriginSlot) {
        console.error(`[SLOT BLOCKED] Cannot create flight: No slots at origin ${origin}`);
        return { error: true, message: `You don't have slots at ${origin}. Negotiate for airport access first.` };
      }
      if (!hasDestSlot) {
        console.error(`[SLOT BLOCKED] Cannot create flight: No slots at destination ${destination}`);
        return { error: true, message: `You don't have slots at ${destination}. Negotiate for airport access first.` };
      }
    }

    // Simple mode with immediateDepart: flight takes off NOW
    const isImmediate = flightData.immediateDepart === true;
    const departureTime = flightData.scheduledDeparture;

    // Simulation mode: flights start as "pending" and require preflight
    // Simple mode: flights start as "scheduled" and can depart immediately
    const initialStatus = isImmediate ? "in_flight" : (isSimpleMode ? "scheduled" : "pending");
    const initialPhase = isImmediate ? "cruise" : (isSimpleMode ? "scheduled" : "pending");
    const initialPreflightComplete = isSimpleMode ? true : false;

    const flight = {
      id: `FLT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      flightNumber: flightData.flightNumber || `${worldState?.airline?.code || 'FL'}${Math.floor(Math.random() * 900) + 100}`,
      origin: flightData.origin,
      destination: flightData.destination,
      aircraftId: flightData.aircraftId,
      aircraftType: flightData.aircraftType,
      eosId: flightData.eosId || null, // For anomalous aircraft behavior matching
      scheduledDeparture: departureTime,
      scheduledArrival: flightData.scheduledArrival,
      // Immediate depart: already in flight, skip scheduling/ground ops
      // Simulation mode: starts as "pending", requires preflight before becoming "scheduled"
      // Simple mode: starts as "scheduled", can depart at scheduled time
      status: initialStatus,
      phase: initialPhase,
      progress: 0,
      distanceFraction: 0,
      totalDelayMinutes: 0,
      delays: [],
      crew: flightData.crew || [],
      passengers: flightData.passengers || 0,
      estimatedRevenue: flightData.estimatedRevenue || 0,
      estimatedCost: flightData.estimatedCost || 0,
      preflight: {
        fueling: !isSimpleMode ? false : true,
        catering: !isSimpleMode ? false : true,
        cleaning: !isSimpleMode ? false : true,
        boarding: !isSimpleMode ? false : true,
        crewBriefing: !isSimpleMode ? false : true,
      },
      preflightComplete: initialPreflightComplete,
      // Immediate depart: already launched
      hasLaunched: isImmediate,
      departureTime: isImmediate ? departureTime : null,
      createdAt: new Date().toISOString(),
      routeId: flightData.routeId,
      // Simple mode: pre-calculated earnings bypass GameLoopEngine formula
      preCalculatedEarnings: flightData.preCalculatedEarnings || null,
      // FUEL ENFORCEMENT: Track if flight was launched beyond aircraft range
      rangeExceeded: flightData.rangeExceeded || false,
      aircraftRange: flightData.aircraftRange || null,
      // ANOMALOUS: Spread any extra properties (timeSlipActive, etc.)
      // This allows anomalous behaviors to attach custom data to flights
      ...(flightData.timeSlipActive && {
        timeSlipActive: flightData.timeSlipActive,
        timeSlipDisappearAt: flightData.timeSlipDisappearAt,
        timeSlipReappearOnDay: flightData.timeSlipReappearOnDay,
        timeSlipModalDisappear: flightData.timeSlipModalDisappear,
        timeSlipModalReappear: flightData.timeSlipModalReappear,
      }),
      // Future anomalous properties can be added here
      ...(flightData.neverAutoComplete && { neverAutoComplete: true }),
      ...(flightData.passiveIncome && { passiveIncome: flightData.passiveIncome }),
      ...(flightData.perpetualStartDay && { perpetualStartDay: flightData.perpetualStartDay }),
      ...(flightData.equatorWaypoints && { equatorWaypoints: flightData.equatorWaypoints }),
      ...(flightData.currentWaypointIndex !== undefined && { currentWaypointIndex: flightData.currentWaypointIndex }),
      ...(flightData.autoRedispatch && { autoRedispatch: true }),
    };

    setWorldState((prev) => ({
      ...prev,
      flights: [...prev.flights, flight],
      // BUG-9 FIX: Mark aircraft as 'in_flight' so it can't be double-assigned
      aircraft: prev.aircraft.map(a =>
        a.id === flightData.aircraftId
          ? { ...a, status: 'in_flight', currentFlightId: flight.id }
          : a
      ),
      stats: {
        ...prev.stats,
        totalFlights: prev.stats.totalFlights + 1,
        dailyDepartures: (prev.stats.dailyDepartures || 0) + 1,
      },
    }));

    debug(`[WorldState] Aircraft ${flightData.aircraftId} now in_flight for ${flight.flightNumber}, dailyDepartures: ${(worldState?.stats?.dailyDepartures || 0) + 1}`);
    return flight;
  }, [worldState?.airline?.code, worldState?.airline?.hub, worldState?.slots, worldState?.gameMode]);

  const addFlight = useCallback((flight) => {
    setWorldState((prev) => ({
      ...prev,
      flights: [...prev.flights, flight],
      stats: {
        ...prev.stats,
        dailyDepartures: (prev.stats?.dailyDepartures || 0) + 1,
      },
    }));
  }, []);

  const updateFlight = useCallback((flightId, updates) => {
    setWorldState((prev) => ({
      ...prev,
      flights: prev.flights.map((f) =>
        f.id === flightId ? { ...f, ...updates } : f
      ),
    }));
  }, []);

  // Add cash to player's balance (used by IAP Cash Store)
  // trackIAP: true = from IAP purchase, false = system grant (default true for IAP tracking)
  const addCash = useCallback((amount, trackIAP = true) => {
    if (!amount || amount <= 0) return;
    debug('[WorldState] Adding cash:', amount, trackIAP ? '(IAP)' : '(system)');
    setWorldState((prev) => ({
      ...prev,
      airline: {
        ...prev.airline,
        cash: (prev.airline?.cash || 0) + amount,
      },
      // Track IAP purchases for OAG leaderboard exclusion
      iap: trackIAP ? {
        totalGranted: (prev.iap?.totalGranted || 0) + amount,
        purchaseCount: (prev.iap?.purchaseCount || 0) + 1,
        lastPurchase: new Date().toISOString(),
      } : prev.iap,
    }));
  }, []);

  // Add Wings to player's balance (used by IAP Cash Store)
  const addWings = useCallback((amount) => {
    if (!amount || amount <= 0) return;
    debug('[WorldState] Adding Wings:', amount);
    setWorldState((prev) => {
      const currentWings = prev.wings || { balance: 20, lifetimeEarned: 20, lifetimeSpent: 0, flightsSinceLastWing: 0 };
      return {
        ...prev,
        wings: {
          ...currentWings,
          balance: (currentWings.balance || 0) + amount,
          lifetimeEarned: (currentWings.lifetimeEarned || 0) + amount,
        },
      };
    });
  }, []);

  const completeFlight = useCallback((flightId, results = {}) => {
    setWorldState((prev) => {
      const flight = prev.flights.find((f) => f.id === flightId);
      if (!flight) return prev;

      // GUARD: If flight is already completed, don't double-credit!
      // GameLoopEngine is the authoritative source for completion now.
      if (flight.phase === "completed" || flight.status === "completed") {
        debug('[WorldState] Flight already completed, skipping:', flightId);
        return prev;
      }

      const isOnTime = (flight.totalDelayMinutes || 0) <= 15;

      // Apply earnings buffs (e.g., Mythic double revenue)
      const buffedResults = applyEarningsBuffs(results, prev.buffs);
      const revenue = buffedResults?.revenue || results.revenue || flight.estimatedRevenue || 0;
      const cost = buffedResults?.costs || results.costs || results.cost || flight.estimatedCost || 0;
      const profit = buffedResults?.profit || (revenue - cost);

      debug('%c✈️ FLIGHT COMPLETED (WorldStateContext) ✈️', 'color: #00ff00; font-weight: bold; font-size: 12px;');
      debug(`%c  Flight: ${flight.flightNumber || flightId}`, 'color: #00ff00;');
      debug(`%c  Route: ${flight.origin} -> ${flight.destination}`, 'color: #00ff00;');
      debug(`%c  dailyFlights BEFORE: ${prev.stats?.dailyFlights || 0}`, 'color: #ffff00;');
      debug(`%c  dailyFlights AFTER:  ${(prev.stats?.dailyFlights || 0) + 1}`, 'color: #00ffff; font-weight: bold;');

      // Update this flight to completed, then cleanup old completed flights
      const updatedFlights = prev.flights.map((f) =>
        f.id === flightId
          ? { ...f, status: "completed", phase: "completed", ...results, revenue, costs: cost }
          : f
      );

      // MEMORY CLEANUP: Keep only active flights + last 200 completed flights
      const activeFlights = updatedFlights.filter(f => f.status !== 'completed' && f.status !== 'cancelled');
      const completedFlights = updatedFlights.filter(f => f.status === 'completed' || f.status === 'cancelled');
      const recentCompleted = completedFlights.slice(-200); // Keep last 200 completed

      // UPDATE AIRCRAFT LOCATION: Move aircraft to destination airport
      const matchingAircraft = prev.aircraft.find(a => a.id === flight.aircraftId);
      if (!matchingAircraft) {
        console.error(`[WorldState] AIRCRAFT NOT FOUND for flight ${flight.id}! aircraftId: ${flight.aircraftId}, available IDs:`, prev.aircraft.map(a => a.id));
      }
      const updatedAircraft = prev.aircraft.map(a => {
        if (a.id === flight.aircraftId) {
          debug(`[WorldState] Aircraft ${a.registration} moved: ${a.location} -> ${flight.destination}`);
          return { ...a, location: flight.destination, status: 'available', currentFlightId: null };
        }
        return a;
      });

      // WINGS: Track flight completion progress toward earning Wings
      const currentWings = prev.wings || { balance: 20, lifetimeEarned: 20, lifetimeSpent: 0, flightsSinceLastWing: 0 };
      const flightsPerWing = 100; // Earn 1 Wing per 100 completed flights
      const newFlightCount = (currentWings.flightsSinceLastWing || 0) + 1;
      const earnedWing = newFlightCount >= flightsPerWing;

      const updatedWings = {
        ...currentWings,
        balance: earnedWing ? currentWings.balance + 1 : currentWings.balance,
        lifetimeEarned: earnedWing ? currentWings.lifetimeEarned + 1 : currentWings.lifetimeEarned,
        flightsSinceLastWing: earnedWing ? 0 : newFlightCount,
      };

      if (earnedWing) {
        debug('[WorldState] Wing earned! New balance:', updatedWings.balance);
      }

      // ROUTE DEMAND: Deplete demand for this route
      const updatedRouteDemand = { ...prev.routeDemand };
      const newDemand = depleteRoute(flight.origin, flight.destination, updatedRouteDemand);
      debug(`[WorldState] Route demand depleted: ${flight.origin}-${flight.destination} -> ${newDemand.toFixed(1)}%`);

      return {
        ...prev,
        flights: [...activeFlights, ...recentCompleted],
        activeFlightIds: prev.activeFlightIds.filter((id) => id !== flightId),
        aircraft: updatedAircraft,
        wings: updatedWings,
        routeDemand: updatedRouteDemand,
        stats: {
          ...prev.stats,
          completedFlights: prev.stats.completedFlights + 1,
          onTimeFlights: prev.stats.onTimeFlights + (isOnTime ? 1 : 0),
          delayedFlights: prev.stats.delayedFlights + (isOnTime ? 0 : 1),
          totalRevenue: prev.stats.totalRevenue + revenue,
          totalCosts: prev.stats.totalCosts + cost,
          totalProfit: prev.stats.totalProfit + profit,
          dailyFlights: (prev.stats.dailyFlights || 0) + 1,
          dailyRevenue: (prev.stats.dailyRevenue || 0) + revenue,
          dailyCosts: (prev.stats.dailyCosts || 0) + cost,
          monthlyFlights: (prev.stats.monthlyFlights || 0) + 1,
          monthlyRevenue: (prev.stats.monthlyRevenue || 0) + revenue,
          monthlyCosts: (prev.stats.monthlyCosts || 0) + cost,
        },
        airline: {
          ...prev.airline,
          cash: prev.airline.cash + profit,
        },
      };
    });
  }, []);

  const cancelFlight = useCallback((flightId, reason) => {
    setWorldState((prev) => {
      // Record cancellation for EOS collapse tracking (use game time)
      const updatedEOS = recordCancellation(prev.eos || createEOSState(), flightId, prev.gameTime);

      return {
        ...prev,
        flights: prev.flights.map((f) =>
          f.id === flightId
            ? { ...f, status: "cancelled", phase: "cancelled", cancelReason: reason, cancelledAt: prev.gameTime || new Date().toISOString() }
            : f
        ),
        stats: {
          ...prev.stats,
          cancelledFlights: prev.stats.cancelledFlights + 1,
          dailyCancellations: (prev.stats.dailyCancellations || 0) + 1,
        },
        eos: updatedEOS,
      };
    });
  }, []);

  const addDelay = useCallback((flightId, minutes, reason) => {
    setWorldState((prev) => ({
      ...prev,
      flights: prev.flights.map((f) =>
        f.id === flightId
          ? {
              ...f,
              totalDelayMinutes: (f.totalDelayMinutes || 0) + minutes,
              delays: [
                ...(f.delays || []),
                { minutes, reason, timestamp: new Date().toISOString() },
              ],
            }
          : f
      ),
    }));
  }, []);

  // ============================================================================
  // WEATHER
  // Update weather for a specific airport (called by WeatherService)
  // ============================================================================
  const updateAirportWeather = useCallback(async (icao) => {
    const airport = AIRPORTS[icao] || FULL_AIRPORTS[icao];
    if (!airport) return null;

    const weatherData = await weatherService.getAirportWeather(icao, airport.lat, airport.lon);
    if (!weatherData) return null;

    const impact = assessWeatherImpact(weatherData);

    setWorldState((prev) => ({
      ...prev,
      airportWeather: {
        ...prev.airportWeather,
        [icao]: {
          data: weatherData,
          impact,
          lastUpdated: new Date().toISOString()
        }
      }
    }));

    return { data: weatherData, impact };
  }, []);

  // Update weather for all active airports (hub + flight origins/destinations)
  const updateActiveAirportsWeather = useCallback(async () => {
    setWorldState((prev) => {
      // Get unique airports from hub + ALL active flights (not just scheduled)
      const airports = new Set([prev.airline.hub]);
      prev.flights.forEach(f => {
        // Include all flights that aren't completed or cancelled
        if (f.phase !== 'completed' && f.phase !== 'cancelled') {
          airports.add(f.origin);
          airports.add(f.destination);
        }
      });

      // Update weather for each (async, but we don't need to wait)
      airports.forEach(icao => {
        updateAirportWeather(icao);
      });

      return prev; // No state change here, updates happen in updateAirportWeather
    });
  }, [updateAirportWeather]);

  // ============================================================================
  // PREFLIGHT & DISPATCH
  // ============================================================================

  const completePreflight = useCallback((flightId, taskId) => {
    setWorldState((prev) => ({
      ...prev,
      flights: prev.flights.map((f) =>
        f.id === flightId
          ? {
              ...f,
              preflight: {
                ...f.preflight,
                [taskId]: true,
              },
            }
          : f
      ),
    }));
  }, []);

  const dispatchFlight = useCallback((flightId) => {
    setWorldState((prev) => ({
      ...prev,
      flights: prev.flights.map((f) =>
        f.id === flightId
          ? {
              ...f,
              status: "scheduled", // Changed from "dispatched" - GameLoopEngine processes "scheduled"
              phase: "scheduled",  // CRITICAL: Must be "scheduled" for GameLoopEngine to pick it up
              preflightComplete: true,
              // Set departure time to now if not already set
              scheduledDeparture: f.scheduledDeparture || new Date().toISOString(),
            }
          : f
      ),
    }));
  }, []);

  // ============================================================================
  // EVENTS
  // ============================================================================

  const addEvent = useCallback((event) => {
    setWorldState((prev) => ({
      ...prev,
      events: [
        ...prev.events.slice(-99),
        { ...event, id: `EVT-${Date.now()}`, timestamp: new Date().toISOString() },
      ],
    }));
  }, []);

  // ============================================================================
  // INBOX (Protocol 13 - The Inbox)
  // ============================================================================

  const addInboxMessage = useCallback((message) => {
    setWorldState((prev) => {
      const newMessage = {
        id: `MSG-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        from: message.from || "SYSTEM",
        fromTitle: message.fromTitle || null,
        subject: message.subject || "No Subject",
        body: message.body || "",
        date: message.date || prev.gameTime,
        read: false,
        type: message.type || "system",
        reliability: message.reliability ?? null,
        stars: message.stars ?? null,
        metadata: message.metadata || {},
      };
      return {
        ...prev,
        inbox: {
          ...prev.inbox,
          messages: [newMessage, ...prev.inbox.messages], // Newest first
        },
      };
    });
  }, []);

  const markMessageRead = useCallback((messageId) => {
    setWorldState((prev) => ({
      ...prev,
      inbox: {
        ...prev.inbox,
        messages: prev.inbox.messages.map((msg) =>
          msg.id === messageId ? { ...msg, read: true } : msg
        ),
      },
    }));
  }, []);

  const markAllMessagesRead = useCallback(() => {
    setWorldState((prev) => ({
      ...prev,
      inbox: {
        ...prev.inbox,
        messages: prev.inbox.messages.map((msg) => ({ ...msg, read: true })),
      },
    }));
  }, []);

  const deleteInboxMessage = useCallback((messageId) => {
    setWorldState((prev) => ({
      ...prev,
      inbox: {
        ...prev.inbox,
        messages: prev.inbox.messages.filter((msg) => msg.id !== messageId),
      },
    }));
  }, []);

  const getUnreadCount = useCallback(() => {
    return worldState.inbox?.messages?.filter((msg) => !msg.read).length || 0;
  }, [worldState.inbox?.messages]);

  // Handle email action button clicks (e.g., "Enter Auction" for hub invites)
  const handleEmailAction = useCallback((action, message) => {
    debug('[WorldState] Email action:', action.type, action);

    if (action.type === 'open_hub_auction') {
      // Re-add hub to pendingHubInvites so WorldMap shows the modal
      const hub = action.hub;
      if (!hub) {
        console.warn('[WorldState] No hub specified for open_hub_auction action');
        return;
      }

      setWorldState((prev) => {
        const pendingInvites = prev.pendingHubInvites || [];
        // Don't add if already pending
        if (pendingInvites.includes(hub)) {
          debug('[WorldState] Hub already pending:', hub);
          return prev;
        }

        // Check if hub is already owned
        const existingHubs = prev.airline?.hubs || ['KPDX'];
        if (existingHubs.includes(hub)) {
          debug('[WorldState] Hub already owned:', hub);
          return prev;
        }

        debug('[WorldState] Re-adding hub to pendingHubInvites:', hub);
        return {
          ...prev,
          pendingHubInvites: [...pendingInvites, hub],
        };
      });

      // Delete the email after action is taken
      deleteInboxMessage(message.id);
    }
  }, [setWorldState, deleteInboxMessage]);

  // ============================================================================
  // INNER CIRCLE (Protocol 11 & 12)
  // ============================================================================

  const hireSpecialist = useCallback((specialist) => {
    setWorldState((prev) => {
      // Check if already hired
      if (prev.innerCircle.hired.some((s) => s.id === specialist.id)) {
        console.warn("[Inner Circle] Specialist already hired:", specialist.id);
        return prev;
      }

      // Check if can afford
      if (prev.airline.cash < specialist.fee) {
        console.warn("[Inner Circle] Not enough cash to hire:", specialist.id);
        return prev;
      }

      const hiredSpecialist = {
        ...specialist,
        hiredAt: prev.gameTime,
        hubBidId: null, // Will be set when hub bid starts
      };

      return {
        ...prev,
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - specialist.fee,
        },
        innerCircle: {
          ...prev.innerCircle,
          hired: [...prev.innerCircle.hired, hiredSpecialist],
        },
        // Add transaction record
        transactions: [
          ...prev.transactions,
          {
            id: `TXN-${Date.now()}`,
            type: "SPECIALIST_HIRE",
            amount: -specialist.fee,
            description: `Hired ${specialist.name} (${specialist.title})`,
            timestamp: prev.gameTime,
          },
        ],
      };
    });
  }, []);

  const fireSpecialist = useCallback((specialistId) => {
    setWorldState((prev) => ({
      ...prev,
      innerCircle: {
        ...prev.innerCircle,
        hired: prev.innerCircle.hired.filter((s) => s.id !== specialistId),
      },
    }));
  }, []);

  const isSpecialistHired = useCallback((specialistId) => {
    return worldState.innerCircle?.hired?.some((s) => s.id === specialistId) || false;
  }, [worldState.innerCircle?.hired]);

  // ============================================================================
  // ROUTES
  // ============================================================================

  const addRoute = useCallback((route) => {
    // Prevent same-airport routes (KPDX -> KPDX)
    if (route.origin === route.destination) {
      console.warn('[addRoute] Rejected same-airport route:', route.origin);
      return;
    }
    setWorldState((prev) => ({
      ...prev,
      routes: [...prev.routes, route],
    }));
  }, []);

  const updateRoute = useCallback((routeId, updates) => {
    debug('[WorldState DEBUG] updateRoute:', { routeId, updates });
    setWorldState((prev) => ({
      ...prev,
      routes: prev.routes.map((r) =>
        r.id === routeId ? { ...r, ...updates } : r
      ),
    }));
  }, []);

  const removeRoute = useCallback((routeId) => {
    setWorldState((prev) => ({
      ...prev,
      routes: prev.routes.filter((r) => r.id !== routeId),
    }));
  }, []);

  // Toggle auto-dispatch for a route
  const toggleRouteAutoDispatch = useCallback((routeId) => {
    setWorldState((prev) => ({
      ...prev,
      routes: prev.routes.map((r) =>
        r.id === routeId ? { ...r, autoDispatch: !r.autoDispatch } : r
      ),
    }));
  }, []);

  // Assign aircraft to a route
  // When aircraft assigned, set scheduledDeparture 30 min in future (OTP pressure)
  // Only in simulation mode - simple/arcade mode doesn't use scheduled departures
  const assignAircraftToRoute = useCallback((routeId, aircraftId) => {
    debug('[WorldState DEBUG] assignAircraftToRoute:', { routeId, aircraftId, stack: new Error().stack?.split('\n').slice(1, 4).join(' <- ') });
    setWorldState((prev) => {
      const gameTime = prev.gameTime ? new Date(prev.gameTime) : new Date();
      const isSimpleMode = prev.gameMode === 'simple' || prev.gameMode === 'arcade';

      // Schedule departure 30 minutes from now when aircraft is assigned (simulation mode only)
      // This creates OTP pressure - player must dispatch before scheduled time or accumulate delay
      // Simple/arcade mode: no scheduledDeparture - player flies manually when ready
      const scheduledDeparture = (!isSimpleMode && aircraftId)
        ? new Date(gameTime.getTime() + 30 * 60 * 1000).toISOString()
        : null;

      return {
        ...prev,
        routes: prev.routes.map((r) =>
          r.id === routeId
            ? {
                ...r,
                aircraftId,
                status: aircraftId ? 'ready' : 'unassigned',
                // OTP pressure fields (simulation mode only)
                scheduledDeparture,
                groundPhase: null,
                groundPhaseStartedAt: null,
                dispatchedAt: null,
              }
            : r
        ),
      };
    });
  }, []);

  // ============================================================================
  // SCHEDULED FLIGHTS
  // ============================================================================

  const addScheduledFlight = useCallback((schedule) => {
    setWorldState((prev) => ({
      ...prev,
      scheduledFlights: [...(prev.scheduledFlights || []), {
        id: `sched-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ...schedule,
        enabled: true,
      }],
    }));
  }, []);

  const removeScheduledFlight = useCallback((scheduleId) => {
    setWorldState((prev) => ({
      ...prev,
      scheduledFlights: (prev.scheduledFlights || []).filter(s => s.id !== scheduleId),
    }));
  }, []);

  // ============================================================================
  // GAME MODE
  // ============================================================================

  const setGameMode = useCallback((mode) => {
    if (mode !== 'simple' && mode !== 'simulation') return;
    setWorldState((prev) => ({
      ...prev,
      gameMode: mode,
    }));
  }, []);

  // ============================================================================
  // SYSTEMS PURCHASE
  // ============================================================================

  const purchaseSystem = useCallback((systemId, cost) => {
    setWorldState((prev) => {
      if (prev.airline.cash < cost) {
        return prev;
      }
      return {
        ...prev,
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - cost,
        },
        systems: {
          ...prev.systems,
          [systemId]: true,
        },
        events: [
          ...prev.events.slice(-99),
          {
            type: "SYSTEM_PURCHASED",
            severity: "success",
            message: `Purchased ${systemId}`,
            timestamp: new Date().toISOString(),
          },
        ],
      };
    });
  }, []);

  // ============================================================================
  // HUB PURCHASE (Simple mode)
  // ============================================================================

  const purchaseHub = useCallback((icao, hubName, price) => {
    setWorldState((prev) => {
      // Validation checks
      if (prev.airline.cash < price) {
        return prev; // Can't afford
      }
      if (prev.ownedHubs?.includes(icao)) {
        return prev; // Already owned
      }

      return {
        ...prev,
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - price,
        },
        ownedHubs: [...new Set([...(prev.ownedHubs || ['KPDX']), icao])],
        events: [
          ...prev.events.slice(-99),
          {
            type: "HUB_PURCHASED",
            severity: "success",
            message: `Welcome to ${hubName}! ${icao} is now your hub.`,
            timestamp: new Date().toISOString(),
          },
        ],
      };
    });
  }, []);

  // ============================================================================
  // NEGOTIATION MANAGEMENT
  // ============================================================================

  const startNegotiation = useCallback((negotiationData) => {
    setWorldState((prev) => ({
      ...prev,
      negotiation: {
        phase: 'legal',
        ...negotiationData,
        triggeredAt: new Date().toISOString(),
        playerBids: [],
        competitorBids: [],
        results: [],
        reputationChanges: [],
        relationshipChanges: [],
        totalCost: 0,
      },
      isPaused: true, // Pause game during negotiation
    }));
  }, []);

  const updateNegotiation = useCallback((updates) => {
    setWorldState((prev) => ({
      ...prev,
      negotiation: prev.negotiation ? { ...prev.negotiation, ...updates } : null,
    }));
  }, []);

  const completeNegotiation = useCallback((results) => {
    setWorldState((prev) => {
      if (!prev.negotiation) return prev;

      const { airportCode, playerBids, results: slotResults, totalCost } = results;
      const slotsWon = slotResults.filter(r => r.winner === 'player');
      const hub = prev.airline?.hub || "KPDX";
      const availableSlots = prev.negotiation.availableSlots || [];

      // Add won slots to player's slots
      const newSlots = { ...prev.slots };
      const newFlights = [...prev.flights];

      if (slotsWon.length > 0) {
        const airportSlots = newSlots[airportCode] || [];

        // Get airport data for coordinates (fallback to FULL_AIRPORTS for regional airports)
        const hubAirport = AIRPORTS[hub] || FULL_AIRPORTS[hub];
        const slotAirport = AIRPORTS[airportCode] || FULL_AIRPORTS[airportCode];

        for (const result of slotsWon) {
          // Add slot to player's slots
          airportSlots.push({
            slotId: result.slotId,
            acquiredAt: new Date().toISOString(),
            monthlyFee: 30000, // Base gate lease
            expiresAt: null, // 6-month minimum then rolling
            lastFlightDate: null,
            firstFlightDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          });

          // ================================================================
          // AUTO-GENERATE OBLIGATORY FLIGHTS FOR WON SLOTS
          // ================================================================
          const slotData = availableSlots.find(s => s.id === result.slotId);
          if (slotData && hubAirport && slotAirport) {
            const gameDate = new Date(prev.gameTime);
            const dateStr = gameDate.toISOString().split('T')[0]; // "2025-01-15"

            // Helper to convert "06:15" to ISO datetime
            const timeToISO = (timeStr) => {
              const [hours, mins] = (timeStr || "00:00").split(":").map(Number);
              const dt = new Date(dateStr + "T00:00:00Z");
              dt.setUTCHours(hours, mins, 0, 0);
              return dt.toISOString();
            };

            // Calculate distance between airports
            const R = 3440.065; // Earth radius in nautical miles
            const lat1 = hubAirport.lat * Math.PI / 180;
            const lat2 = slotAirport.lat * Math.PI / 180;
            const dLat = (slotAirport.lat - hubAirport.lat) * Math.PI / 180;
            const dLon = (slotAirport.lon - hubAirport.lon) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(lat1) * Math.cos(lat2) *
                      Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            const distance = Math.round(R * c);

            // Get first available aircraft
            const availableAircraft = prev.aircraft.find(ac => ac.status === 'available');
            const aircraftId = availableAircraft?.id || null;
            const aircraftType = availableAircraft?.model || availableAircraft?.aircraftType || "E175";

            const flightIdBase = `SLOT-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
            const airlineCode = prev.airline?.code || "PNW";

            // FLIGHT 1: Positioning flight (Hub -> Slot Airport)
            // Departs hub at hubDepartureRequired time
            const positioningFlight = {
              id: `${flightIdBase}-POS`,
              flightNumber: `${airlineCode}${Math.floor(Math.random() * 900) + 100}`,
              origin: hub,
              destination: airportCode,
              originLat: hubAirport.lat,
              originLon: hubAirport.lon,
              destLat: slotAirport.lat,
              destLon: slotAirport.lon,
              aircraftId,
              aircraftType,
              distance,
              scheduledDeparture: timeToISO(slotData.hubDepartureRequired),
              scheduledArrival: timeToISO(slotData.departTime), // Arrive before slot time
              status: "scheduled",
              phase: "scheduled",
              progress: 0,
              totalDelayMinutes: 0,
              delays: [],
              crew: [],
              passengers: 0,
              estimatedRevenue: 0, // Positioning = no revenue (deadhead)
              estimatedCost: Math.round(distance * 8), // Fuel + ops cost
              preflight: {
                fueling: false,
                catering: false,
                cleaning: false,
                boarding: false,
                crewBriefing: false,
              },
              preflightComplete: false,
              isSlotObligation: true,
              slotId: result.slotId,
              isPositioning: true,
              createdAt: new Date().toISOString(),
            };

            // FLIGHT 2: Slot Obligation flight (Slot Airport -> Hub)
            // This is the actual revenue flight - departs at slot time
            const slotFlight = {
              id: `${flightIdBase}-SVC`,
              flightNumber: `${airlineCode}${Math.floor(Math.random() * 900) + 100}`,
              origin: airportCode,
              destination: hub,
              originLat: slotAirport.lat,
              originLon: slotAirport.lon,
              destLat: hubAirport.lat,
              destLon: hubAirport.lon,
              aircraftId,
              aircraftType,
              distance,
              scheduledDeparture: timeToISO(slotData.departTime),
              scheduledArrival: timeToISO(slotData.hubReturnTime || slotData.returnTime),
              status: "scheduled",
              phase: "scheduled",
              progress: 0,
              totalDelayMinutes: 0,
              delays: [],
              crew: [],
              passengers: Math.floor(Math.random() * 50) + 30, // Simulated passengers
              estimatedRevenue: Math.round(distance * 0.15 * 80), // ~$0.15/nm * ~80 pax
              estimatedCost: Math.round(distance * 8),
              preflight: {
                fueling: false,
                catering: false,
                cleaning: false,
                boarding: false,
                crewBriefing: false,
              },
              preflightComplete: false,
              isSlotObligation: true,
              slotId: result.slotId,
              isPositioning: false,
              createdAt: new Date().toISOString(),
            };

            newFlights.push(positioningFlight);
            newFlights.push(slotFlight);

            debug(`[SLOT OBLIGATION] Auto-generated flights for ${result.slotId}:`, {
              positioning: `${hub}->${airportCode} @ ${slotData.hubDepartureRequired}`,
              service: `${airportCode}->${hub} @ ${slotData.departTime}`,
            });
          }
        }
        newSlots[airportCode] = airportSlots;
      }

      // Deduct cost
      const newCash = prev.airline.cash - totalCost;

      // Add to history
      const historyEntry = {
        ...prev.negotiation,
        ...results,
        completedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        negotiation: null,
        slots: newSlots,
        flights: newFlights,
        airline: {
          ...prev.airline,
          cash: newCash,
        },
        negotiationHistory: [...prev.negotiationHistory, historyEntry],
        // Resume game
        isPaused: false,
      };
    });
  }, []);

  const cancelNegotiation = useCallback(() => {
    setWorldState((prev) => ({
      ...prev,
      negotiation: null,
      isPaused: false,
    }));
  }, []);

  const updateLegalState = useCallback((updates) => {
    setWorldState((prev) => ({
      ...prev,
      legal: prev.legal ? { ...prev.legal, ...updates } : updates,
    }));
  }, []);

  // ============================================================================
  // SLOT VALIDATION HELPERS
  // ============================================================================

  // Check if player has slots at an airport
  const hasSlotAt = useCallback((airportCode) => {
    const slots = worldState?.slots?.[airportCode];
    return slots && slots.length > 0;
  }, [worldState?.slots]);

  // Check if a flight can be scheduled (validates slots at both ends)
  const canScheduleFlightTo = useCallback((origin, destination) => {
    const hub = worldState?.airline?.hub || "KPDX";

    // Hub always has "slots" (home base operations)
    const hasOriginSlot = origin === hub || hasSlotAt(origin);
    const hasDestSlot = destination === hub || hasSlotAt(destination);

    if (!hasOriginSlot && !hasDestSlot) {
      return { canSchedule: false, reason: `You don't have slots at ${origin} or ${destination}. Negotiate for airport access first.` };
    }
    if (!hasOriginSlot) {
      return { canSchedule: false, reason: `You don't have slots at ${origin}. Negotiate for airport access first.` };
    }
    if (!hasDestSlot) {
      return { canSchedule: false, reason: `You don't have slots at ${destination}. Negotiate for airport access first.` };
    }

    return { canSchedule: true, reason: null };
  }, [worldState?.airline?.hub, hasSlotAt]);

  // Get list of airports where player has slots (including hub)
  const getAirportsWithSlots = useCallback(() => {
    const hub = worldState?.airline?.hub || "KPDX";
    const slotAirports = Object.keys(worldState?.slots || {}).filter(
      code => worldState.slots[code]?.length > 0
    );
    // Always include hub
    if (!slotAirports.includes(hub)) {
      slotAirports.unshift(hub);
    }
    return slotAirports;
  }, [worldState?.airline?.hub, worldState?.slots]);

  // ============================================================================
  // SLOT SCHEDULING (Simulation Mode)
  // ============================================================================

  // Initialize simulation mode slots with departure times
  const initializeSimulationSlots = useCallback(() => {
    setWorldState((prev) => {
      if (prev.gameMode !== 'simulation') {
        console.warn('[WorldState] initializeSimulationSlots called outside simulation mode');
        return prev;
      }
      const simulationSlots = generateSimulationSlots();
      debug('[WorldState] Initialized simulation slots:', simulationSlots);
      return {
        ...prev,
        slots: simulationSlots,
      };
    });
  }, []);

  // Assign aircraft, crew, and destination to a slot
  const assignSlot = useCallback((airportCode, slotId, assignment) => {
    setWorldState((prev) => {
      const airportSlots = prev.slots[airportCode];
      if (!airportSlots) {
        console.error(`[WorldState] No slots at airport ${airportCode}`);
        return prev;
      }

      const slotIndex = airportSlots.findIndex(s => s.slotId === slotId);
      if (slotIndex === -1) {
        console.error(`[WorldState] Slot ${slotId} not found at ${airportCode}`);
        return prev;
      }

      const slot = airportSlots[slotIndex];
      const updatedSlot = {
        ...slot,
        assignedAircraftId: assignment.aircraftId,
        assignedCrewId: assignment.crewId, // For backwards compat (captain ID)
        assignedCrew: assignment.crew || null, // Full crew: { captainId, foId, faIds }
        assignedDestination: assignment.destination,
        assignedFlightId: assignment.flightId || null,
        assignmentDate: prev.dayNumber,
        status: SLOT_STATUS.ASSIGNED,
      };

      const updatedSlots = {
        ...prev.slots,
        [airportCode]: [
          ...airportSlots.slice(0, slotIndex),
          updatedSlot,
          ...airportSlots.slice(slotIndex + 1),
        ],
      };

      debug(`[WorldState] Slot ${slotId} assigned:`, updatedSlot);
      return {
        ...prev,
        slots: updatedSlots,
      };
    });
  }, []);

  // Clear slot assignment (for editing/canceling)
  const clearSlotAssignment = useCallback((airportCode, slotId) => {
    setWorldState((prev) => {
      const airportSlots = prev.slots[airportCode];
      if (!airportSlots) return prev;

      const slotIndex = airportSlots.findIndex(s => s.slotId === slotId);
      if (slotIndex === -1) return prev;

      const slot = airportSlots[slotIndex];
      const updatedSlot = {
        ...slot,
        assignedAircraftId: null,
        assignedCrewId: null,
        assignedCrew: null, // Clear full crew object
        assignedDestination: null,
        assignedFlightId: null,
        assignmentDate: null,
        status: SLOT_STATUS.AVAILABLE,
      };

      const updatedSlots = {
        ...prev.slots,
        [airportCode]: [
          ...airportSlots.slice(0, slotIndex),
          updatedSlot,
          ...airportSlots.slice(slotIndex + 1),
        ],
      };

      return {
        ...prev,
        slots: updatedSlots,
      };
    });
  }, []);

  // Update slot status (for warnings, delays, etc.)
  const updateSlotStatus = useCallback((airportCode, slotId, status) => {
    setWorldState((prev) => {
      const airportSlots = prev.slots[airportCode];
      if (!airportSlots) return prev;

      const slotIndex = airportSlots.findIndex(s => s.slotId === slotId);
      if (slotIndex === -1) return prev;

      const updatedSlots = {
        ...prev.slots,
        [airportCode]: [
          ...airportSlots.slice(0, slotIndex),
          { ...airportSlots[slotIndex], status },
          ...airportSlots.slice(slotIndex + 1),
        ],
      };

      return {
        ...prev,
        slots: updatedSlots,
      };
    });
  }, []);

  // Update slot with scheduled flight ID
  const updateSlotFlightId = useCallback((airportCode, slotId, flightId) => {
    setWorldState((prev) => {
      const airportSlots = prev.slots[airportCode];
      if (!airportSlots) return prev;

      const slotIndex = airportSlots.findIndex(s => s.slotId === slotId);
      if (slotIndex === -1) return prev;

      const updatedSlots = {
        ...prev.slots,
        [airportCode]: [
          ...airportSlots.slice(0, slotIndex),
          { ...airportSlots[slotIndex], assignedFlightId: flightId },
          ...airportSlots.slice(slotIndex + 1),
        ],
      };

      debug(`[WorldState] Slot ${slotId} linked to flight ${flightId}`);
      return {
        ...prev,
        slots: updatedSlots,
      };
    });
  }, []);

  // Reset all slot assignments for a new day
  const resetDailySlotAssignments = useCallback(() => {
    setWorldState((prev) => {
      const resetSlots = {};
      for (const [airport, slots] of Object.entries(prev.slots)) {
        resetSlots[airport] = slots.map(slot => ({
          ...slot,
          assignedAircraftId: null,
          assignedCrewId: null,
          assignedDestination: null,
          assignedFlightId: null,
          assignmentDate: null,
          status: SLOT_STATUS.AVAILABLE,
        }));
      }
      return {
        ...prev,
        slots: resetSlots,
      };
    });
  }, []);

  // Get slots for a specific airport sorted by departure time
  const getSlotsForAirport = useCallback((airportCode) => {
    const slots = worldState?.slots?.[airportCode] || [];
    return [...slots].sort((a, b) => {
      if (!a.departureTime || !b.departureTime) return 0;
      return a.departureTime.localeCompare(b.departureTime);
    });
  }, [worldState?.slots]);

  // ============================================================================
  // FLEET PROGRESSION (Simulation mode only)
  // Check and update aircraft tier unlocks based on network growth
  // ============================================================================

  const checkAndUpdateFleetUnlocks = useCallback(() => {
    // Only check in simulation mode
    if (worldState?.gameMode !== 'simulation') return { newUnlocks: [] };

    const { unlocks, newUnlocks } = checkFleetUnlocks(worldState);

    if (newUnlocks.length > 0) {
      setWorldState((prev) => ({
        ...prev,
        fleetUnlocks: unlocks,
        // Store pending unlock for notification display
        pendingFleetUnlock: newUnlocks[0], // Show first unlock
      }));
      debug('[FleetProgression] New unlocks:', newUnlocks);
    }

    return { newUnlocks };
  }, [worldState]);

  // Dismiss fleet unlock notification
  const dismissFleetUnlock = useCallback(() => {
    setWorldState((prev) => ({
      ...prev,
      pendingFleetUnlock: null,
    }));
  }, []);

  // Get current fleet unlocks state
  const getFleetUnlocks = useCallback(() => {
    return worldState?.fleetUnlocks || DEFAULT_FLEET_UNLOCKS;
  }, [worldState?.fleetUnlocks]);

  // ============================================================================
  // AIRPORT UNLOCK SYSTEM
  // ============================================================================

  const unlockAirport = useCallback((airportCode, reason) => {
    setWorldState((prev) => ({
      ...prev,
      unlockedAirports: {
        ...prev.unlockedAirports,
        [airportCode]: {
          unlockReason: reason,
          unlockedAt: new Date().toISOString(),
        },
      },
    }));
  }, []);

  const purchaseHRD = useCallback((airportCode, cost) => {
    setWorldState((prev) => {
      if (prev.airline.cash < cost) return prev;

      return {
        ...prev,
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - cost,
        },
        unlockedAirports: {
          ...prev.unlockedAirports,
          [airportCode]: {
            unlockReason: "hrd_purchase",
            unlockedAt: new Date().toISOString(),
          },
        },
        hrdPurchases: [
          ...prev.hrdPurchases,
          { airportCode, purchasedAt: new Date().toISOString(), cost },
        ],
        events: [
          ...prev.events.slice(-99),
          {
            type: "HRD_PURCHASED",
            severity: "success",
            message: `Purchased market intel for ${airportCode} - negotiation now available`,
            timestamp: new Date().toISOString(),
          },
        ],
      };
    });
  }, []);

  // ============================================================================
  // HUB RELOCATION
  // ============================================================================

  const relocateHub = useCallback((newHub, cost) => {
    setWorldState((prev) => {
      if (prev.airline.cash < cost) {
        return prev;
      }
      return {
        ...prev,
        airline: {
          ...prev.airline,
          hub: newHub,
          cash: prev.airline.cash - cost,
        },
        aircraft: prev.aircraft.map((a) => ({
          ...a,
          location: newHub,
          homeBase: newHub,
        })),
        events: [
          ...prev.events.slice(-99),
          {
            type: "HUB_RELOCATED",
            severity: "info",
            message: `Hub relocated to ${AIRPORTS[newHub]?.city || newHub}`,
            timestamp: new Date().toISOString(),
          },
        ],
      };
    });
  }, []);

  // ============================================================================
  // SCHEDULE GENERATION (called by GameLoopEngine)
  // ============================================================================

  const setTodaySchedule = useCallback((flights) => {
    setWorldState((prev) => {
      // MEMORY CLEANUP: Keep only last 200 completed flights (consistent with completeFlight)
      const completedFlights = prev.flights.filter((f) => f.phase === "completed" || f.status === "completed");
      const recentCompleted = completedFlights.slice(-200);
      return {
        ...prev,
        flights: [...recentCompleted, ...flights],
        todaySchedule: flights.map((f) => f.id),
        lastScheduleGeneratedDay: prev.dayNumber,
      };
    });
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // Force reload from save file
  const reloadSave = useCallback(async () => {
    hasLoadedFromFileRef.current = false;
    setSaveLoaded(false);
  }, []);

  // Force save now
  const forceSave = useCallback(async () => {
    return await saveGame(worldState);
  }, [worldState]);

  const value = {
    worldState,
    setWorldState,
    updateWorldState,
    resetWorldState,

    // Save system
    saveLoaded,
    reloadSave,
    forceSave,
    steamUser,
    setContinuedFromMenu,

    // Time
    setGameSpeed,
    togglePause,

    // Cash
    updateCash,
    addTransaction,

    // Fleet
    addAircraft,
    updateAircraft,
    removeAircraft,

    // Leases
    addLease,
    updateLease,

    // Crew
    addCrew,
    updateCrew,

    // Relations
    updateRelations,

    // Flights
    createFlight,
    addFlight,
    updateFlight,
    completeFlight,
    addCash,
    addWings,
    cancelFlight,
    addDelay,
    completePreflight,
    dispatchFlight,

    // Weather
    updateAirportWeather,
    updateActiveAirportsWeather,

    // Events
    addEvent,

    // Inbox (Protocol 13)
    addInboxMessage,
    markMessageRead,
    markAllMessagesRead,
    deleteInboxMessage,
    getUnreadCount,
    handleEmailAction,

    // Inner Circle (Protocol 11 & 12)
    hireSpecialist,
    fireSpecialist,
    isSpecialistHired,

    // Routes
    addRoute,
    updateRoute,
    removeRoute,
    toggleRouteAutoDispatch,
    assignAircraftToRoute,

    // Scheduled Flights
    addScheduledFlight,
    removeScheduledFlight,

    // Game Mode
    setGameMode,

    // Systems
    purchaseSystem,

    // Hub
    relocateHub,
    purchaseHub,

    // Schedule
    setTodaySchedule,

    // Negotiations
    startNegotiation,
    updateNegotiation,
    completeNegotiation,
    cancelNegotiation,
    updateLegalState,

    // Airport unlocks
    unlockAirport,
    purchaseHRD,

    // Slot validation
    hasSlotAt,
    canScheduleFlightTo,
    getAirportsWithSlots,

    // Slot scheduling (Simulation Mode)
    initializeSimulationSlots,
    assignSlot,
    clearSlotAssignment,
    updateSlotStatus,
    updateSlotFlightId,
    resetDailySlotAssignments,
    getSlotsForAirport,

    // Fleet progression (Simulation Mode)
    checkAndUpdateFleetUnlocks,
    dismissFleetUnlock,
    getFleetUnlocks,
  };

  return (
    <WorldStateContext.Provider value={value}>
      {children}
    </WorldStateContext.Provider>
  );
}

export default WorldStateContext;